import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InputShortText from "../../../../components/InputShortText/InputShortText";
import Notice from "../../../../components/Notice/Notice";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import SmallCheckBox from "../../../../components/SmallCheckBox/SmallCheckBox";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import TextArea from "../../../../components/TextArea/TextArea";
import { EmailHtmlDualEditor } from "../../../../components/EmailHtmlDualEditor/EmailHtmlDualEditor";
import { NotificationDeliveryToggle } from "../../../../components/NotificationDeliveryToggle/NotificationDeliveryToggle";
import TinyText from "../../../../components/TinyText/TinyText";
import RemindersAPI from "../../../../api/RemindersAPI";
import { toastService } from "../../../../services/ToastService";
import { normalizeEmailBodyFormat, type EmailBodyFormat } from "../../../../types/emailBodyFormat";
import { isValidText } from "../../../../shared/validation";
import type { NotificationChannel } from "../../../../components/NotificationChannelToggle/NotificationChannelToggle";
import { UpdateCallback } from "../../sections/NotificationsSegment";
import { stableSignatureBookingConfirmation } from "../reminderConfigStableSignature";

export interface BookingConfirmationEmailConfig {
  enabled: boolean;
  type?: NotificationChannel;
  emailTitle?: string;
  text?: string;
  emailBodyFormat?: EmailBodyFormat;
}

interface Props {
  update: UpdateCallback;
  defaultConfig?: BookingConfirmationEmailConfig;
  hideSectionTitle?: boolean;
}

export function BookingConfirmationEmail({ update, defaultConfig, hideSectionTitle }: Props) {
  const [enabled, setEnabled] = useState(defaultConfig?.enabled ?? false);
  const [emailTitle, setEmailTitle] = useState(defaultConfig?.emailTitle ?? "");
  const [text, setText] = useState(defaultConfig?.text ?? "");
  const [emailBodyFormat, setEmailBodyFormat] = useState<EmailBodyFormat | undefined>(
    defaultConfig?.emailBodyFormat
  );
  const [testLoading, setTestLoading] = useState(false);
  const { t } = useTranslation();
  const lastEmittedSignatureRef = useRef<string | null>(null);

  const fmt = normalizeEmailBodyFormat(emailBodyFormat);

  useEffect(() => {
    if (!defaultConfig) return;
    const incoming = stableSignatureBookingConfirmation(defaultConfig);
    if (lastEmittedSignatureRef.current !== null && incoming === lastEmittedSignatureRef.current) {
      return;
    }
    setEnabled(defaultConfig.enabled ?? false);
    setEmailTitle(defaultConfig.emailTitle ?? "");
    setText(defaultConfig.text ?? "");
    setEmailBodyFormat(defaultConfig.emailBodyFormat);
  }, [defaultConfig]);

  useEffect(() => {
    const payload = {
      enabled,
      type: "email" as const,
      emailTitle,
      text,
      emailBodyFormat: fmt,
    };
    lastEmittedSignatureRef.current = stableSignatureBookingConfirmation(payload);
    update("bookingConfirmationEmail", payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, emailTitle, text, fmt]);

  const injectDefaultTemplate = () => {
    setEmailTitle(String(t("notifications.booking_confirmation.default_title")));
    setText(
      String(
        fmt === "html"
          ? t("notifications.booking_confirmation.default_html")
          : t("notifications.booking_confirmation.default_plain")
      )
    );
  };

  const sendTest = async () => {
    if (!emailTitle.trim() || !text.trim()) {
      toastService.showToast(t("notifications.test_email_validation"), "error");
      return;
    }
    setTestLoading(true);
    try {
      await RemindersAPI.sendTestEmail({
        subject: emailTitle.trim(),
        htmlBody: text,
        emailBodyFormat: fmt,
      });
      toastService.showToast(t("notifications.test_email_ok"));
    } catch {
      toastService.showToast(t("notifications.test_email_fail"), "error");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div>
      {!hideSectionTitle && <Subtitle text={t("notifications.booking_confirmation")} />}
      <TinyText text={t("notifications.booking_confirmation.description")} />
      <SmallCheckBox
        label={t("global.enabled")!}
        onChange={() => setEnabled((prev) => !prev)}
        defaultValue={enabled}
      />
      <br />
      <div className="mt-3">
        <p className="text-sm font-extrabold">{t("notifications.delivery.label")}:</p>
        <NotificationDeliveryToggle
          variant="three_sms_disabled"
          className="mt-2"
          value={fmt}
          onChange={setEmailBodyFormat}
        />
      </div>
      <Notice pillText={t("global.notice")} tinyText={t("notifications.booking_confirmation.keywords")} />
      <div className="mt-3 space-y-3">
        <InputShortText
          label={t("notifications.booking_confirmation.email_title")!}
          validationFn={(value) => value === "" || isValidText(value, 1, 200)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailTitle(e.target.value)}
          errorMessage={"errors.too_long"}
          defaultValue={emailTitle}
        />
        {fmt === "plain" ? (
          <TextArea
            label={t("notifications.booking_confirmation.email_plain_label")!}
            onChange={(e) => setText(e.currentTarget.value)}
            defaultValue={text}
            errorMessage={"errors.too_long"}
            maxLength={10000}
          />
        ) : (
          <div className="mt-1">
            <EmailHtmlDualEditor
              label={t("notifications.booking_confirmation.email_html")!}
              value={text}
              onChange={setText}
              maxLength={100000}
            />
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <SecondaryButton
          text={String(t("notifications.booking_confirmation.use_default_template"))}
          fn={injectDefaultTemplate}
        />
        <SecondaryButton
          text={String(t("notifications.test_email"))}
          fn={sendTest}
          isDisabled={testLoading}
        />
      </div>
    </div>
  );
}
